import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Spinner } from '@adop/ui';
import { MailCheck } from 'lucide-react';
import type { InviteAcceptResponse } from '@adop/shared';
import { postJson, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { Alert } from '../components/Alert';
import { rememberInviteToken } from '../lib/invite-token';

export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const { loading, user, me, signInWithGoogle, signOut, refresh } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (user || !token) return;
    rememberInviteToken(token);
    setStarting(true);
    void signInWithGoogle().catch((cause: unknown) => {
      setError(errorMessage(cause));
      setStarting(false);
    });
  }, [loading, user, token, signInWithGoogle]);

  const accept = async () => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await postJson<InviteAcceptResponse>('/api/platform/invites/accept', { token });
      await refresh();
      navigate('/dashboard', { replace: true });
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const pendingMatch = me?.pending_invites[0];

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 px-4">
      <div className="w-full max-w-md rounded-xl border border-zinc-200 bg-white p-8 text-center shadow-sm">
        <MailCheck className="mx-auto h-9 w-9 text-zinc-400" />
        <h1 className="mt-3 text-lg font-semibold text-zinc-900">You&apos;ve been invited</h1>
        {starting || loading ? (
          <div className="mt-4 flex justify-center">
            <Spinner size="lg" />
          </div>
        ) : !user ? (
          <div className="mt-4 space-y-3">
            {error ? <Alert>{error}</Alert> : null}
            <Button loading={starting} onClick={() => (token ? (rememberInviteToken(token), signInWithGoogle()) : signInWithGoogle())}>
              Sign in with Google to accept
            </Button>
          </div>
        ) : !token ? (
          <Alert>This invitation link is missing its token. Ask your manager to resend it.</Alert>
        ) : (
          <div className="mt-4 space-y-4">
            <p className="text-sm text-zinc-600">
              {pendingMatch ? (
                <>
                  Accept the invitation to <strong>{pendingMatch.restaurant_name}</strong> as{' '}
                  <strong>{pendingMatch.role}</strong>?
                </>
              ) : (
                'Accept this invitation to join the restaurant?'
              )}
            </p>
            {error ? <Alert>{error}</Alert> : null}
            <div className="flex justify-center gap-2">
              <Button loading={busy} onClick={accept}>
                Accept invitation
              </Button>
              <Button variant="outline" onClick={() => signOut()}>
                Not now
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
